import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getMeetingFacets, listMeetings } from "../../services/explorerService";
import MeetingHistoryCard from "./MeetingHistoryCard";
import { groupByDay } from "./meetingHistoryUtils";
import "../../styles/meetingHistory.css";
import "./meetingHistoryLanding.css";

const PAGE_SIZE = 12;

function ChevronLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 3.5L5.5 8 10 12.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12.2 12.2L15.5 15.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="1.5" y="1.5" width="5" height="5" rx="1.2" />
      <rect x="9.5" y="1.5" width="5" height="5" rx="1.2" />
      <rect x="1.5" y="9.5" width="5" height="5" rx="1.2" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1.2" />
    </svg>
  );
}

function RowsIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 4h12M2 8h12M2 12h12"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FilterSelect({ value, onChange, options, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`mg-hist-select${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="mg-hist-select__btn"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{selected?.label}</span>
      </button>
      {open && (
        <ul
          className="mg-hist-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((opt) => (
            <li key={opt.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`mg-hist-select__option${opt.value === value ? " is-active" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function dateFromForRange(range) {
  const days =
    range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 0;
  if (!days) return undefined;
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function mergeItems(prev, next) {
  const seen = new Set();
  const out = [];
  for (const item of [...prev, ...next]) {
    const key = item?.roomName;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export default function MeetingHistoryPage({
  companyId,
  recruiterId,
  onBack,
  onOpenMeeting,
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [timeRange, setTimeRange] = useState("all");
  const [hostFilter, setHostFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("any");
  const [view, setView] = useState("grid");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hosts, setHosts] = useState([]);
  const [scopeStats, setScopeStats] = useState({
    total: 0,
    totalMeetingSeconds: 0,
    thisMonth: 0,
  });
  const requestIdRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const searchQ = debouncedQ.trim();
  const dateFrom = dateFromForRange(timeRange);
  const hasMore = items.length < total;

  // Debounce search input
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), q ? 280 : 0);
    return () => window.clearTimeout(t);
  }, [q]);

  // Full-scope hosts + header stats (not limited to current page)
  useEffect(() => {
    if (!companyId || !recruiterId) {
      setHosts([]);
      setScopeStats({ total: 0, totalMeetingSeconds: 0, thisMonth: 0 });
      return undefined;
    }
    let cancelled = false;
    getMeetingFacets({ companyId, recruiterId, status: "ended" })
      .then((res) => {
        if (cancelled) return;
        setHosts(Array.isArray(res.hosts) ? res.hosts : []);
        setScopeStats({
          total: Number(res.total) || 0,
          totalMeetingSeconds: Number(res.totalMeetingSeconds) || 0,
          thisMonth: Number(res.thisMonth) || 0,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setHosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, recruiterId]);

  const fetchPage = useCallback(
    async ({ reset }) => {
      if (!companyId || !recruiterId) {
        setItems([]);
        setTotal(0);
        setError("");
        return;
      }

      const requestId = ++requestIdRef.current;
      const skip = reset ? 0 : itemsRef.current.length;

      if (reset) {
        setLoading(true);
        setError("");
      } else {
        setLoadingMore(true);
      }

      try {
        const res = await listMeetings({
          q: searchQ,
          status: "ended",
          limit: PAGE_SIZE,
          skip,
          companyId,
          recruiterId,
          dateFrom,
          host: hostFilter,
          duration: durationFilter,
        });
        if (requestId !== requestIdRef.current) return;

        const pageItems = Array.isArray(res.items) ? res.items : [];
        setTotal(Number(res.total) || 0);
        setItems((prev) => (reset ? pageItems : mergeItems(prev, pageItems)));
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Failed to load meetings");
        if (reset) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [companyId, recruiterId, searchQ, dateFrom, hostFilter, durationFilter],
  );

  // Reset + load first page when scope / search / filters change
  useEffect(() => {
    if (!companyId || !recruiterId) {
      setItems([]);
      setTotal(0);
      return undefined;
    }
    fetchPage({ reset: true });
    return undefined;
  }, [
    companyId,
    recruiterId,
    searchQ,
    dateFrom,
    hostFilter,
    durationFilter,
    fetchPage,
  ]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    fetchPage({ reset: false });
  }, [fetchPage, hasMore, loading, loadingMore]);

  const groups = useMemo(() => groupByDay(items), [items]);

  const totalHours = (scopeStats.totalMeetingSeconds / 3600).toFixed(1);
  const thisMonth = scopeStats.thisMonth;
  const showingLoaded = items.length;
  const filtersActive =
    timeRange !== "all" ||
    hostFilter !== "all" ||
    durationFilter !== "any" ||
    Boolean(searchQ);
  const searchPending = q.trim() !== debouncedQ.trim();
  const isRefreshing = loading || searchPending;
  const hasFilterOrSearch =
    timeRange !== "all" ||
    hostFilter !== "all" ||
    durationFilter !== "any" ||
    Boolean(searchQ) ||
    Boolean(q.trim());

  let statusLabel = "";
  if (searchPending || (loading && Boolean(q.trim() || searchQ))) {
    statusLabel = "Searching…";
  } else if (loading && hasFilterOrSearch) {
    statusLabel = "Updating results…";
  } else if (loading) {
    statusLabel = "Loading meetings…";
  } else if (loadingMore) {
    statusLabel = "Loading more…";
  }

  const showInitialLoader = Boolean(
    companyId && recruiterId && loading && !items.length && !hasFilterOrSearch,
  );
  const showResultsChrome = Boolean(
    companyId &&
    recruiterId &&
    (items.length > 0 || isRefreshing || (!loading && !error)),
  );

  return (
    <div className="mg-hist-page">
      <header className="mg-hist-page__head">
        <div className="mg-hist-page__title-block">
          <button
            type="button"
            className="mg-hist-back"
            onClick={onBack}
            aria-label="Back"
          >
            <ChevronLeftIcon />
          </button>
          <div>
            <h1>Meeting History</h1>
            <p>All recordings, summaries &amp; transcripts in one place</p>
          </div>
        </div>
        <div className="mg-hist-stats">
          <span className="mg-hist-stat">
            <i className="mg-hist-stat__dot is-violet" />
            {scopeStats.total} total meetings
          </span>
          <span className="mg-hist-stat">
            <i className="mg-hist-stat__dot is-teal" />
            {totalHours} hrs recorded
          </span>
          <span className="mg-hist-stat">
            <i className="mg-hist-stat__dot is-green" />
            {thisMonth} this month
          </span>
        </div>
      </header>

      <form
        className={`mg-hist-filters${isRefreshing ? " is-busy" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          setDebouncedQ(q.trim());
          fetchPage({ reset: true });
        }}
      >
        <label className="mg-hist-search">
          <span className="mg-hist-search__icon">
            {isRefreshing ? (
              <span className="mg-hist-spinner" aria-hidden />
            ) : (
              <SearchIcon />
            )}
          </span>
          <input
            type="search"
            placeholder="Search by title, host, or participant…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-busy={isRefreshing}
          />
        </label>
        <div className="mg-hist-filter-pills">
          <FilterSelect
            ariaLabel="Time range"
            value={timeRange}
            onChange={setTimeRange}
            options={[
              { value: "all", label: "All time" },
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
            ]}
          />
          <FilterSelect
            ariaLabel="Host"
            value={hostFilter}
            onChange={setHostFilter}
            options={[
              { value: "all", label: "All hosts" },
              ...hosts.map((name) => ({ value: name, label: name })),
            ]}
          />
          <FilterSelect
            ariaLabel="Duration"
            value={durationFilter}
            onChange={setDurationFilter}
            options={[
              { value: "any", label: "Any duration" },
              { value: "short", label: "< 30 min" },
              { value: "medium", label: "30–60 min" },
              { value: "long", label: "60+ min" },
            ]}
          />
        </div>
      </form>
      {!companyId || !recruiterId ? (
        <p className="mg-hist-error">
          Open MeetGeek from GetGeek with your company and recruiter context to
          view meeting history.
        </p>
      ) : null}
      {showInitialLoader && (
        <div className="mg-hist-loading-block" role="status" aria-live="polite">
          <span className="mg-hist-spinner mg-hist-spinner--lg" aria-hidden />
          <p>Loading meetings…</p>
        </div>
      )}
      {!loading && error && !items.length && (
        <p className="mg-hist-error">{error}</p>
      )}
      {showResultsChrome && !showInitialLoader && (
        <>
          <div className="mg-hist-toolbar">
            <p className="mg-hist-count">
              {isRefreshing ? (
                <>
                  <span
                    className="mg-hist-spinner mg-hist-spinner--sm"
                    aria-hidden
                  />
                  <span role="status" aria-live="polite">
                    {statusLabel || "Updating results…"}
                  </span>
                </>
              ) : (
                <span className="mg-hist-count-label">
                  <strong>{showingLoaded}</strong>
                  {`${total > showingLoaded ? ` of ${total}` : ""} ${
                    total === 1 ? "meeting" : "meetings"
                  }${total > showingLoaded ? " loaded" : " found"}`}
                </span>
              )}
            </p>
            <div
              className="mg-hist-view-toggle"
              role="group"
              aria-label="View mode"
            >
              <button
                type="button"
                className={view === "grid" ? "is-active" : ""}
                aria-pressed={view === "grid"}
                aria-label="Grid view"
                onClick={() => setView("grid")}
              >
                <GridIcon />
              </button>
              <button
                type="button"
                className={view === "list" ? "is-active" : ""}
                aria-pressed={view === "list"}
                aria-label="List view"
                onClick={() => setView("list")}
              >
                <RowsIcon />
              </button>
            </div>
          </div>

          <div
            className={`mg-hist-results${isRefreshing ? " is-loading" : ""}`}
            aria-busy={isRefreshing}
          >
            {groups.map(([label, dayItems]) => (
              <section key={label} className="mg-hist-group">
                <div className="mg-hist-group__head">
                  <h2>{label}</h2>
                  <span className="mg-hist-group__line" />
                </div>
                <div
                  className={view === "list" ? "mg-hist-list" : "mg-hist-grid"}
                >
                  {dayItems.map((item) => (
                    <MeetingHistoryCard
                      key={item.roomName}
                      item={item}
                      variant="detail"
                      layout={view}
                      onOpen={onOpenMeeting}
                    />
                  ))}
                </div>
              </section>
            ))}

            {!isRefreshing && !groups.length && (
              <p className="mg-hist-muted">
                {filtersActive
                  ? "No meetings match your search or filters."
                  : "No meetings yet for this account."}
              </p>
            )}
          </div>

          {error && items.length > 0 && (
            <p className="mg-hist-error">{error}</p>
          )}

          {hasMore && !isRefreshing && (
            <div className="mg-hist-pagination">
              <button
                type="button"
                className="mg-hist-load-more"
                onClick={loadMore}
                disabled={loadingMore || loading}
              >
                {loadingMore ? "Loading…" : "Load more meetings"}
              </button>
              <p className="mg-hist-pagination__hint">
                Showing {showingLoaded} of {total}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
